import { useMemo } from "react";
import fp from "lodash/fp";
import { useSelector } from "react-redux";

import { store } from "helpers";

import { permissionList } from "./permissionList";

export function hasPermissions(requiredPermissions) {
	if (!requiredPermissions) {
		return true;
	}

	if (!Array.isArray(requiredPermissions)) {
		requiredPermissions = [requiredPermissions];
	}

	const permissions = !fp.isEmpty(store.getState().user.permissions)
		? store.getState().user.permissions.map(p => p.name)
		: null;
	if (!permissions || permissions.length === 0) {
		return false;
	}

	const hasPermissions =
		fp.intersection(permissions, requiredPermissions).length > 0;

	return hasPermissions;
}

export function hasAllPermissions(requiredPermissions) {
	if (!requiredPermissions) {
		return true;
	}

	if (!Array.isArray(requiredPermissions)) {
		requiredPermissions = [requiredPermissions];
	}

	const permissions = !fp.isEmpty(store.getState().user.permissions)
		? store.getState().user.permissions.map(p => p.name)
		: null;
	if (!permissions || permissions.length === 0) {
		return false;
	}

	const hasPermissions =
		fp.intersection(permissions, requiredPermissions).length ===
		requiredPermissions.length;

	return hasPermissions;
}

export function useRequiredPermissions(requiredPermissions) {
	if (requiredPermissions && !Array.isArray(requiredPermissions)) {
		requiredPermissions = [requiredPermissions];
	}

	const userPermissions = useSelector(state =>
		state.user.permissions.map(p => p.name),
	);

	return useMemo(() => {
		if (!requiredPermissions || requiredPermissions.length === 0) {
			return true;
		}

		if (userPermissions.length === 0) {
			return false;
		}

		const hasPermissions =
			fp.intersection(userPermissions, requiredPermissions).length > 0;

		return hasPermissions;
	}, [userPermissions, requiredPermissions]);
}

export function hasAccessToAttachmentType(attachmentType) {
	const type = attachmentTypePermissions.find(({ enumNumber, name }) => {
		return [enumNumber, name].includes(attachmentType);
	});

	if (!type) return true;

	return hasPermissions(type.requiredPermissions);
}

export function maskSensitiveAccountData(str) {
	if (!str) return str;

	const hasViewFull = hasPermissions(
		permissionList.ViewSensitiveAccountDataFull,
	);

	const hasViewPartial = hasPermissions(
		permissionList.ViewSensitiveAccountDataPartial,
	);

	if (hasViewFull) {
		return str;
	}

	if (hasViewPartial) {
		return str.replace(/.(?=.{4})/g, "*");
	}
	return str.replace(/./g, "*");
}

const attachmentTypePermissions = [
	{
		enumNumber: 0,
		name: "Bank Letter",
		requiredPermissions: [
			permissionList.ViewLevel1,
			permissionList.ViewLevel2,
			permissionList.ViewLevel3,
		],
	},
	{
		enumNumber: 1,
		name: "Photo Identification",
		requiredPermissions: [
			permissionList.ViewLevel1,
			permissionList.ViewLevel2,
		],
	},
	{
		enumNumber: 2,
		name: "Tax Return",
		requiredPermissions: [
			permissionList.ViewLevel1,
			permissionList.ViewLevel2,
			permissionList.ViewLevel3,
		],
	},
	{
		enumNumber: 3,
		name: "Bank Statement",
		requiredPermissions: [
			permissionList.ViewLevel1,
			permissionList.ViewLevel2,
			permissionList.ViewLevel3,
		],
	},
	{
		enumNumber: 4,
		name: "Voided Check",
		requiredPermissions: [
			permissionList.ViewLevel1,
			permissionList.ViewLevel2,
			permissionList.ViewLevel3,
		],
	},
	{
		enumNumber: 5,
		name: "Business License",
		requiredPermissions: [
			permissionList.ViewLevel1,
			permissionList.ViewLevel2,
			permissionList.ViewLevel3,
			permissionList.ViewLevel4,
		],
	},
	{
		enumNumber: 6,
		name: "Utility Bill",
		requiredPermissions: [
			permissionList.ViewLevel1,
			permissionList.ViewLevel2,
			permissionList.ViewLevel3,
			permissionList.ViewLevel4,
		],
	},
	{
		enumNumber: 7,
		name: "Previous Processor Statements",
		requiredPermissions: [
			permissionList.ViewLevel1,
			permissionList.ViewLevel2,
			permissionList.ViewLevel3,
		],
	},
	{
		enumNumber: 8,
		name: "Application (Unredacted)",
		requiredPermissions: [
			permissionList.ViewLevel1,
			permissionList.ViewLevel2,
		],
	},
	{
		enumNumber: 9,
		name: "Other Underwriting Documentation",
		requiredPermissions: [
			permissionList.ViewLevel1,
			permissionList.ViewLevel2,
			permissionList.ViewLevel3,
			permissionList.ViewLevel4,
		],
	},
	{
		enumNumber: 10,
		name: "Equipment Ordering Form",
		requiredPermissions: [
			permissionList.ViewLevel1,
			permissionList.ViewLevel2,
			permissionList.ViewLevel3,
			permissionList.ViewLevel4,
		],
	},
	{
		enumNumber: 11,
		name: "Underwriting Document",
		requiredPermissions: [permissionList.ViewLevel1],
	},
];
